import Peer from 'peerjs';

const log = (message, ...args) => console.log(`[PeerService] ${message}`, ...args);
const logError = (message, ...args) => console.error(`[PeerService] ${message}`, ...args);

class PeerService {
	constructor({ onCall, onCallClose, onCallError, onDisconnected, onStream, onOpen }) {
		this.peer = null;
		this.config = {
			host: process.env.NEXT_PUBLIC_PEER_HOST || '0.peerjs.com',
			port: process.env.NEXT_PUBLIC_PEER_PORT || 443,
			path: process.env.NEXT_PUBLIC_PEER_PATH || '/',
			secure: process.env.NEXT_PUBLIC_PEER_SECURE !== 'false',
			debug: process.env.NODE_ENV === 'development' ? 3 : 0,
			config: {
				iceServers: [
					{ urls: ["stun:fr-turn4.xirsys.com"] },
					{
						username: "hHnF_it349oCryJskVyQQjcIQD6wsjCb8vJGeeuseCt-IgR2b35W-zbtM4koRHrVAAAAAGh4QNV0cW5lZW4=",
						credential: "4437cb34-62a3-11f0-8baa-465dc79a0fbd",
						urls: ["turn:fr-turn4.xirsys.com:80?transport=udp",
							"turn:fr-turn4.xirsys.com:3478?transport=udp",
							"turn:fr-turn4.xirsys.com:80?transport=tcp",
							"turn:fr-turn4.xirsys.com:3478?transport=tcp",
							"turns:fr-turn4.xirsys.com:443?transport=tcp",
							"turns:fr-turn4.xirsys.com:5349?transport=tcp"]
					}]
			}
		};
		this.onCall = onCall;
		this.onCallClose = onCallClose;
		this.onCallError = onCallError;
		this.onDisconnected = onDisconnected;
		this.onStream = onStream;
		this.onOpen = onOpen;
	}

	initialize(userId) {
		if (this.peer && !this.peer.destroyed) {
			log('Peer already initialized.');
			return;
		}
		log(`Initializing PeerJS for userId: ${userId}`);
		this.peer = new Peer(userId, this.config);
		this.attachEvents();
	}

	attachEvents() {
		this.peer.on('open', (id) => {
			log(`PeerJS connection opened. Peer ID: ${id}`);
			if (this.onOpen) this.onOpen(id);
		});

		this.peer.on('call', (call) => {
			log(`Incoming call from peerId: ${call.peer}`);
			if (this.onCall) this.onCall(call);
		});

		this.peer.on('error', (err) => {
			logError(`A peer error occurred: ${err.type}`, err);
			this.handlePeerError(err);
		});

		this.peer.on('disconnected', () => {
			log('PeerJS disconnected.');
			if (this.onDisconnected) this.onDisconnected();
		});

		this.peer.on('close', () => {
			log('PeerJS connection closed.');
			this.destroy();
		});
	}

	handlePeerError(err) {
		const errorMessages = {
			'peer-unavailable': `المستخدم غير متاح حالياً`,
			'network': 'فشل الاتصال بالشبكة. يرجى التحقق من اتصالك بالإنترنت.',
			'server-error': 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.',
			'webrtc': 'حدث خطأ في اتصال WebRTC. قد تحتاج إلى تحديث الصفحة.',
			'disconnected': 'انقطع الاتصال بخادم المكالمات.',
			'browser-incompatible': 'متصفحك غير متوافق مع خدمة المكالمات.',
		};

		const message = errorMessages[err.type] || 'حدث خطأ غير متوقع في الاتصال.';

		if (['network', 'server-error', 'disconnected'].includes(err.type)) {
			if (this.onDisconnected) this.onDisconnected(message);
		} else {
			if (this.onCallError) this.onCallError(err, message);
		}
	}

	async makeCall(remotePeerId, stream, metadata) {
		log(`Calling remote peer ${remotePeerId}`);
		if (!this.peer) {
			throw new Error('Peer is not initialized.');
		}
		const call = this.peer.call(remotePeerId, stream, { metadata });
		if (!call) {
			throw new Error("Failed to create a call object with PeerJS.");
		}
		this.attachCallEvents(call);
		return call;
	}

	async answerCall(call, stream) {
		log('Answering call...');
		call.answer(stream);
		this.attachCallEvents(call);
	}

	attachCallEvents(call) {
		call.on('stream', (remoteStream) => {
			log('Received remote audio stream.');
			if (this.onStream) this.onStream(remoteStream);
		});

		call.on('close', () => {
			log('Call closed by remote peer.');
			if (this.onCallClose) this.onCallClose();
		});

		call.on('error', (err) => {
			logError(`Call failed with error: ${err.type}`, err);
			if (this.onCallError) this.onCallError(err, 'حدث خطأ أثناء المكالمة');
		});
	}

	reconnect(userId) {
		if (this.peer && !this.peer.destroyed) {
			log('Reconnecting existing peer instance.');
			this.peer.reconnect();
		} else {
			log('Peer instance destroyed, re-initializing.');
			this.initialize(userId);
		}
	}

	destroy() {
		if (this.peer) {
			log('Destroying PeerJS instance.');
			if (!this.peer.destroyed) {
				this.peer.destroy();
			}
			this.peer = null;
		}
	}
}

export default PeerService;
